/*
  cib-favorites — centralized favorites (wishlist) store.

  Storage approach: localStorage under the key `cib:favorites:v1`, holding a
  JSON array of product-id strings. Chosen over IndexedDB because the data is a
  small flat list; localStorage is synchronous, universally supported, and
  survives refresh/reopen. If storage is unavailable (private mode, disabled,
  or a non-browser context) the store degrades to in-memory for the session.

  API (window.cibFavorites):
    has(id)    -> boolean
    add(id)    -> true            (idempotent; dedupes)
    remove(id) -> false           (idempotent)
    toggle(id) -> boolean         (the NEW state)
    all()      -> string[]

  Sync: every mutation persists and dispatches `cib:favorites:change` on
  `document` with { productId, active, ids } so every rendered card (any page,
  any section) can update. A `storage` listener syncs across browser tabs
  (productId: null means "bulk change — re-check your own state").
*/
(function () {
  if (typeof window === 'undefined' || window.cibFavorites) return;

  var STORAGE_KEY = 'cib:favorites:v1';

  function storageAvailable() {
    try {
      var t = '__cib_fav_test__';
      window.localStorage.setItem(t, t);
      window.localStorage.removeItem(t);
      return true;
    } catch (e) {
      return false;
    }
  }

  var canPersist = storageAvailable();

  function load() {
    if (!canPersist) return new Set();
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) return new Set();
      return new Set(
        arr.map(function (v) { return String(v); }).filter(Boolean)
      );
    } catch (e) {
      return new Set(); // corrupted data — start clean
    }
  }

  var ids = load();

  function persist() {
    if (!canPersist) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(ids)));
    } catch (e) {
      /* quota/denied — keep in-memory state */
    }
  }

  function emit(productId, active) {
    document.dispatchEvent(new CustomEvent('cib:favorites:change', {
      detail: { productId: productId, active: active, ids: Array.from(ids) }
    }));
  }

  window.cibFavorites = {
    has: function (id) {
      return ids.has(String(id));
    },
    all: function () {
      return Array.from(ids);
    },
    add: function (id) {
      id = String(id || '');
      if (!id) return false;
      if (!ids.has(id)) {
        ids.add(id);
        persist();
        emit(id, true);
      }
      return true;
    },
    remove: function (id) {
      id = String(id || '');
      if (ids.has(id)) {
        ids.delete(id);
        persist();
        emit(id, false);
      }
      return false;
    },
    toggle: function (id) {
      return this.has(id) ? this.remove(id) : this.add(id);
    }
  };

  // Cross-tab synchronization.
  if (canPersist) {
    window.addEventListener('storage', function (e) {
      if (e.key !== STORAGE_KEY) return;
      ids = load();
      emit(null, null); // listeners re-check their own product's state
    });
  }
})();
